import { memo, useEffect } from "react";
import { APP_NAME, installAppLabel } from "../../../config/brand";
import { C } from "../../match/shared/matchTokens";
import { notifyInstallBannerVisible } from "../../analytics/integrations/pwaIntegration";

const bannerStyle = {
  position: "fixed",
  left: "50%",
  transform: "translateX(-50%)",
  bottom: "calc(12px + env(safe-area-inset-bottom))",
  width: "min(480px, calc(100% - 24px))",
  zIndex: 60,
  background: "rgba(13,20,64,0.96)",
  border: `1px solid ${C.bdr}`,
  borderRadius: 16,
  padding: "12px 14px",
  boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
  fontFamily: "var(--tra-font-family-body)",
};

/**
 * Subtle install CTA for lobby only (never shown during an active session).
 * @param {import('../hooks/usePwaInstall').PwaInstallState} install
 */
function PwaInstallBanner({ install }) {
  const {
    canShowInstall,
    canNativePrompt,
    iosHint,
    menuInstallHint,
    promptInstall,
    dismiss,
  } = install;

  useEffect(() => {
    if (!canShowInstall) return;
    notifyInstallBannerVisible();
  }, [canShowInstall]);

  if (!canShowInstall) return null;

  return (
    <aside
      className="pwa-install-banner"
      style={bannerStyle}
      role="region"
      aria-label={installAppLabel()}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <img
          src={`${process.env.PUBLIC_URL || ""}/icons/icon-96.png`}
          alt=""
          width={48}
          height={48}
          style={{ borderRadius: 12, flexShrink: 0 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 800,
              fontSize: 14,
              color: C.lav,
              marginBottom: 4,
            }}
          >
            {installAppLabel()}
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 12,
              lineHeight: 1.45,
              color: C.txtM,
            }}
          >
            {iosHint
              ? "On iPhone or iPad: tap Share, then Add to Home Screen for a full-screen app."
              : menuInstallHint
                ? `Use the install icon in the address bar, or browser menu (⋮) → ${installAppLabel()}. Refresh once if you don't see it.`
                : "Add to your home screen for faster access and offline scoring."}
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install suggestion"
          style={{
            border: "none",
            background: "transparent",
            color: C.txtD,
            cursor: "pointer",
            fontSize: 18,
            lineHeight: 1,
            padding: 4,
          }}
        >
          ×
        </button>
      </div>
      {canNativePrompt && (
        <button
          type="button"
          className="pwa-install-btn"
          onClick={() => promptInstall()}
          style={{
            marginTop: 10,
            width: "100%",
            padding: "10px 12px",
            borderRadius: 12,
            border: "none",
            background: C.purple,
            color: "#fff",
            fontWeight: 800,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          {installAppLabel()}
        </button>
      )}
    </aside>
  );
}

export default memo(PwaInstallBanner);
